import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import { AppComponentProps, SetupStep, SetupStepType } from '../typescriptHelpers';
import {
    selectHasCompletedQualitativeOnboardingAtLeastOnce,
    selectIsAppsDemoModeEnabled,
    selectIsUserflowOnboardingEnabledForOrg,
} from '../selectors/selectors';
import TaskList from './setup/TaskList';
import { SETUP_STEP_BY_TYPE, useSetupStepsSequence } from './setup/hooks';
import Alert from 'antd/lib/alert';
import Text from 'antd/lib/typography/Text';
import { Link, useLocation } from 'react-router-dom';
import { createAppSettingsRoute } from '@/helpers/routeHelpers';
import Button from 'antd/lib/button';
import * as Storage from '../helpers/localStorageHelper';
import { trackUserEvent } from '@/actions/trackEvent';
import { Event } from '../utils/analytics';
import { tryUpdateAppSettings } from '@/actions/appSettings';
import { AppSettingsUpdatable } from '@/actions/updatable';

interface QualitativeOnboardingTaskListProps extends AppComponentProps {
    shouldForceShowOnboarding?: boolean;
}

export const QualitativeOnboardingTaskList: React.FC<QualitativeOnboardingTaskListProps> = ({
    orgId,
    appId,
    shouldForceShowOnboarding,
}) => {
    const someoneHasCompletedQualitativeOnboardingAtLeastOnce = useSelector((state: RootState) =>
        selectHasCompletedQualitativeOnboardingAtLeastOnce(state, appId),
    );

    const dispatch = useDispatch();

    const [onboardingDismissed, setOnboardingDismissed] = React.useState(Storage.getOnboardingDismissedForAppId(appId));

    const { onboardingStepsSequenceRecord, onboardingCompletedSteps, onboardingProgress } = useOnboardingProgress(
        orgId,
        appId,
    );

    const location = useLocation();

    const shouldShowSuccessBanner = location.state?.['shouldShowSuccessBanner'] ?? false;

    const shouldShowOnboarding =
        useShouldShowOnboarding(orgId, appId, onboardingDismissed) || shouldForceShowOnboarding;

    const shouldUseUserflowOnboarding = useSelector((state: RootState) =>
        selectIsUserflowOnboardingEnabledForOrg(state, orgId),
    );

    const saveDismissedOnboarding = (isDismissed: boolean) => {
        // record onboarding dismissed in local storage
        setOnboardingDismissed(isDismissed);
        Storage.setOnboardingDismissedForAppId(appId, isDismissed);

        dispatch(
            trackUserEvent({
                orgId,
                appId,
                eventName: isDismissed ? Event.ClickDismissOnboarding : Event.ClickViewOnboarding,
            }),
        );
    };

    React.useEffect(() => {
        if (!someoneHasCompletedQualitativeOnboardingAtLeastOnce && onboardingProgress === 100) {
            // persist onboarding completed status to the BE the first time onboarding is completed or even if it's completed but the hasCompletedQualitativeOnboardingAtLeastOnce property hasn't been set to true.
            dispatch(
                tryUpdateAppSettings(AppSettingsUpdatable.UpdateQualitativeOnboardingStatus, {
                    appId,
                    orgId,
                    isComplete: true,
                }),
            );
        }

        if (shouldShowSuccessBanner && onboardingProgress === 100) {
            dispatch(trackUserEvent({ orgId: orgId, appId: appId, eventName: Event.CompleteOnboardingSteps }));
        }
    }, [shouldShowSuccessBanner, onboardingProgress]);

    return (
        <>
            {shouldShowSuccessBanner && onboardingProgress === 100 && (
                <Alert
                    style={{
                        marginBottom: 36,
                    }}
                    message="Great, you're all set up!"
                    description={
                        <Text>
                            For more configuration and integration options, go to{' '}
                            <Link to={createAppSettingsRoute(orgId, appId)}>
                                <Button type="link" target={'_blank'} shape="circle" style={{ padding: 0, height: 0 }}>
                                    App Settings
                                </Button>
                            </Link>
                        </Text>
                    }
                    type="success"
                    showIcon
                />
            )}

            {shouldShowOnboarding && !shouldUseUserflowOnboarding && (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        marginBottom: 36,
                        flexGrow: 1,
                    }}
                >
                    <TaskList
                        shouldHideHeader={shouldForceShowOnboarding}
                        appId={appId}
                        stepsSequence={onboardingStepsSequenceRecord}
                        completedSteps={onboardingCompletedSteps}
                        progress={onboardingProgress}
                        onDismiss={() => {
                            saveDismissedOnboarding(true);
                        }}
                    />
                </div>
            )}
        </>
    );
};

export const useShouldShowOnboarding = (orgId: string, appId: string, onboardingDismissed: boolean): boolean => {
    const { onboardingProgress } = useOnboardingProgress(orgId, appId);

    const someoneHasCompletedQualitativeOnboardingAtLeastOnce = useSelector((state: RootState) =>
        selectHasCompletedQualitativeOnboardingAtLeastOnce(state, appId),
    );

    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));

    return (
        onboardingProgress < 100 &&
        !onboardingDismissed &&
        !isViewingAppsInDemoMode &&
        !someoneHasCompletedQualitativeOnboardingAtLeastOnce
    );
};

const useOnboardingProgress = (orgId: string, appId: string) => {
    const onboardingStepsSequence = useSetupStepsSequence(appId);

    const onboardingStepsSequenceRecord = onboardingStepsSequence.reduce((cur, key) => {
        return Object.assign(cur, { [key]: SETUP_STEP_BY_TYPE[key] });
    }, {}) as Record<SetupStepType, SetupStep>;

    const onboardingCompletedSteps = useSelector((state: RootState) =>
        Object.entries(onboardingStepsSequenceRecord)
            .filter(([, step]) => {
                return step.isCompleteSelector(state, { appId: appId, orgId: orgId });
            })
            .map((step) => step[0]),
    );

    const onboardingProgress = React.useMemo(() => {
        if (
            onboardingCompletedSteps.length === 0 ||
            !onboardingStepsSequenceRecord ||
            Object.keys(onboardingStepsSequenceRecord).length === 0
        ) {
            return 0;
        }

        return (onboardingCompletedSteps.length / Object.keys(onboardingStepsSequenceRecord).length) * 100;
    }, [onboardingCompletedSteps]);

    return { onboardingStepsSequenceRecord, onboardingCompletedSteps, onboardingProgress };
};
